import { SplitPanel } from "@amzn/awsui-components-react/polaris";
import { FC, ReactNode } from "react";
import { usePanelStore, ViewerPanelId } from "../../state";
import { MapEngineHandle } from "../../react";
import LayersPanel from "./LayersPanel";
import ZoomToTilePanel from "./ZoomToTilePanel";
import ZoomToXYPanel from "./ZoomToXYPanel";
import SpritesPanel from "./SpritesPanel";
import GeoJsonPanel from "./GeoJsonPanel";
import BboxPanel from "./BboxPanel";
import FeatureInspectPanel from "./FeatureInspectPanel";

const TITLES: Record<ViewerPanelId, string> = {
    layers: "Layers",
    sprites: "Stylesheet — Sprites",
    "zoom-to-tile": "Zoom to Tile",
    "zoom-to-xy": "Zoom to XY",
    geojson: "GeoJSON",
    bbox: "BBox",
    "feature-inspect": "Feature Details",
};

const I18N = {
    preferencesTitle: "Split panel preferences",
    preferencesPositionLabel: "Split panel position",
    preferencesPositionDescription: "Choose where the panel docks.",
    preferencesPositionSide: "Side",
    preferencesPositionBottom: "Bottom",
    preferencesConfirm: "Confirm",
    preferencesCancel: "Cancel",
    closeButtonAriaLabel: "Close panel",
    openButtonAriaLabel: "Open panel",
    resizeHandleAriaLabel: "Resize panel",
};

/**
 * The v2 panels rendered inside the native Cloudscape SplitPanel (resize +
 * side/bottom for free). Mounted by the shared Layout's AppLayout splitPanel
 * slot, but ONLY on viewer-v2 routes. Reads the open panel + engine handle from
 * usePanelStore so it needs no props from Layout.
 */
const ViewerSplitPanelContent: FC = () => {
    const openPanel = usePanelStore((s) => s.openPanel);
    const handle = usePanelStore((s) => s.engineHandle) as MapEngineHandle | null;

    if (!openPanel) return null;

    const body: ReactNode = (() => {
        switch (openPanel) {
            case "layers": return <LayersPanel handle={handle} />;
            case "zoom-to-tile": return <ZoomToTilePanel handle={handle} />;
            case "zoom-to-xy": return <ZoomToXYPanel handle={handle} />;
            case "sprites": return <SpritesPanel handle={handle} />;
            case "geojson": return <GeoJsonPanel handle={handle} />;
            case "bbox": return <BboxPanel handle={handle} />;
            case "feature-inspect": return <FeatureInspectPanel />;
            default: return null;
        }
    })();

    return (
        <SplitPanel header={TITLES[openPanel]} i18nStrings={I18N}>
            {body}
        </SplitPanel>
    );
};

export default ViewerSplitPanelContent;
